<script setup>
import { inject, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import NProgress from "nprogress";
import { ChevronRightIcon } from "@heroicons/vue/20/solid";
import Pagination from "@Components/Pagination.vue";
import AppLink from "@Components/AppLink.vue";
import { formatMetrics, getType } from "@Utils/project";
import { useForm } from "@/form";
import { MagicIcon } from "@Icons/index";
import GlobalMetricsCard from "./Partials/GlobalMetricsCard.vue";
import { useI18n } from "vue-i18n";
import Breadcrumb from "@Components/Breadcrumb.vue";
import _filter from "lodash/filter";
import { PrimaryButton } from "@Components/Button";
import Badge from "@Components/Badge.vue";
import { BrainCircuitIcon, PlusIcon } from "lucide-vue-next";

const router = useRouter();
const route = useRoute();
const { t: __ } = useI18n();

const globalRefs = inject("globalRefs");

const projects = ref();
const selectedProjects = ref([]);

const form = useForm({
    page: route.query.page ? parseInt(route.query.page) : 1,
    sort_order: route.query?.sort_order,
    sort_direction: route.query?.sort_direction,
    enabled: route.query?.enabled,
    type: route.query?.type,
});

const processing = ref(false);

// methods
const fetch = async () => {
    NProgress.start();
    processing.value = true;
    projects.value = await form.post("projects_index");
    selectedProjects.value = [];
    processing.value = false;
    NProgress.done();
};

// watchers
watch(
    () => form.data(),
    async () => {
        router.push({ name: "projects.index", query: form.data() });
        await fetch();
    },
    { deep: true }
);

watch(
    () => route.query,
    (params) => {
        if (params.p) {
            form.page = parseInt(params.p);
        }
    }
);
// ---

await fetch();

const interval = ref();
onMounted(() => {
    interval.value = setInterval(() => {
        fetch();
    }, 15 * 1000);
});

onUnmounted(() => {
    clearInterval(interval.value);
});
</script>

<template>
    <div>
        <Breadcrumb :pages="[{ name: __('Mes projets') }]">
            <PrimaryButton @click="globalRefs.toolsMenu.open('autopilot')" :icon="PlusIcon">
                {{ __("Nouveau projet") }}
            </PrimaryButton>
        </Breadcrumb>

        <template v-if="projects.data.length > 0">
            <div class="relative grid sm:grid-cols-4">
                <GlobalMetricsCard class="border-b border-gray-300 dark:border-gray-800 sm:col-span-4" />
            </div>

            <div class="divide-y divide-gray-100 dark:divide-gray-900">
                <AppLink
                    :to="{ name: 'publications.index', params: { id: project.id } }"
                    v-for="project in projects.data"
                    :key="project.id"
                    class="relative flex items-center px-4 py-4 space-x-4 dark:hover:bg-gray-900 hover:bg-gray-50 lg:px-8 sm:px-6">
                    <div class="min-w-0 flex-auto">
                        <div>
                            <div class="flex items-center gap-x-3">
                                <div
                                    :class="[
                                        project.enabled ? 'text-green-400 bg-green-400/10' : 'text-rose-400 bg-rose-400/10',
                                        'flex-none rounded-full p-1',
                                    ]">
                                    <div class="w-2 h-2 bg-current rounded-full" />
                                </div>
                                <h2 class="min-w-0 text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                                    <div class="flex gap-x-2">
                                        <span class="truncate">{{ project.name }}</span>
                                        <span class="text-gray-600 dark:text-gray-400">/</span>
                                        <span class="whitespace-nowrap">{{ __(getType(project.type)?.label) }}</span>
                                    </div>
                                </h2>
                            </div>
                            <div
                                v-if="['news', 'rss'].indexOf(project.type) !== -1 && project.modules.autopilot?.query"
                                class="mt-1.5 ml-7 text-xs text-gray-900 truncate dark:text-white">
                                {{ project.modules.autopilot.query }}
                            </div>
                        </div>
                        <div class="flex items-center gap-x-2.5 mt-3 text-xs leading-5 text-gray-600 dark:text-gray-400">
                            <template v-if="project.persona">
                                <div class="flex items-center gap-x-4">
                                    <img :src="project.persona.user_avatar_url" class="w-8 h-8 bg-gray-800 rounded-full dark:bg-gray-700" />
                                    <div class="text-sm font-medium leading-6 truncate">{{ project.persona.user_full_name }}</div>
                                </div>
                                <svg viewBox="0 0 2 2" class="w-0.5 h-0.5 flex-none fill-gray-300 dark:fill-gray-700">
                                    <circle cx="1" cy="1" r="1" />
                                </svg>
                            </template>
                        </div>
                    </div>
                    <div class="hidden grid-cols-2 xl:grid">
                        <div
                            v-for="(stat, statIdx) in _filter(formatMetrics(project.metrics), (m) => ['total', 'published'].indexOf(m.key) !== -1)"
                            :key="stat.name"
                            :class="[statIdx % 2 === 1 ? 'border-l' : '', statIdx > 1 ? 'border-t' : '', 'border-gray-300 dark:border-gray-800 p-3']">
                            <p class="text-sm font-medium leading-6 text-gray-600 dark:text-gray-400">{{ __(stat.name) }}</p>
                            <p class="flex items-baseline gap-x-2 mt-2">
                                <span class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">{{ stat.value }}</span>
                                <span v-if="stat.unit" class="text-sm text-gray-600 dark:text-gray-400">{{ stat.unit }}</span>
                            </p>
                        </div>
                    </div>
                    <Badge v-if="project.is_complete" color="green">{{ __("Complété") }}</Badge>
                    <Badge v-else color="yellow">{{ __("En cours") }}</Badge>
                    <ChevronRightIcon class="w-5 h-5 flex-none text-gray-600 dark:text-gray-400" aria-hidden="true" />
                </AppLink>
            </div>

            <div class="px-4 py-4 border-t border-gray-300 dark:border-gray-800 lg:px-8 md:px-6 sm:px-6">
                <Pagination v-if="projects.last_page > 1" v-model="form.page" :items="projects" :disabled="processing" />
            </div>
        </template>
        <div v-else class="mx-auto max-w-xl py-16">
            <button
                @click="globalRefs.toolsMenu.open('autopilot')"
                type="button"
                class="w-full relative block p-12 text-center rounded-lg border-2 border-gray-300 border-dashed group dark:border-gray-700 dark:hover:border-white focus:border-gray-900 focus:outline-none hover:border-gray-900">
                <BrainCircuitIcon class="mx-auto w-12 h-12 text-gray-600 dark:text-gray-400" />
                <div class="mt-6 text-lg font-semibold text-gray-900 dark:text-white">{{ __("Aucun projet") }}</div>
                <div class="mt-3 text-sm text-gray-600 dark:text-gray-400">{{ __("Vous n'avez pas encore créé de projet.") }}</div>
                <div class="flex justify-center mt-8">
                    <PrimaryButton :icon="MagicIcon">
                        {{ __("Génération Autopilot") }}
                    </PrimaryButton>
                </div>
            </button>
        </div>
    </div>
</template>
